<template>
  <div class="user-list">
    <s-table
      class="user-list__table"
      :fields="fields"
      :items="items"
      :busy="loading"
      :sort-by="sortBy.field"
      :sort-desc="sortBy.desc"
      @sort-changed="sort($event)"
    >
      <template v-for="field in fields" #[`head(${field.key})`]>
        {{
          field.labelPath
            ? $t('user-manager.query.list.column-titles.' + field.labelPath)
            : null
        }}
      </template>
      <template #cell(profiles)="data">
        <list-aggregator
          :modal-title="data.item.name"
          :modal-table-items="data.item.profiles"
          :modal-table-header="
            $t('user-manager.query.list.column-titles.access-profiles')
          "
          suppressedLabelPath="user-manager.query.list.suppressed-profiles"
        />
      </template>
      <template #cell(status)="data">
        <span class="user-list__captalized" :class="getStatusClass(data.item)">
          {{ $t('user-manager.query.list.status.' + getStatusMod(data.item)) }}
        </span>
      </template>
      <template #cell(level)="data">
        {{ $t(`permissions.levels.${data.item.level}`) }}
      </template>
      <template #cell(actions)="data">
        <b-button
          @click="editUser(data.item)"
          variant="outline-primary"
          size="sm"
        >
          <span class="fa fa-edit" />
          {{ $t('user-manager.query.list.edit') }}
        </b-button>
        <b-button
          @click="deleteUser(data.item)"
          :disabled="userIsLoggedUser(data.item)"
          variant="primary"
          size="sm"
        >
          <span class="far fa-trash-alt" />
          {{ $t('user-manager.query.list.delete') }}
        </b-button>
        <b-dropdown
          class="user-list__options-btn"
          variant="link"
          offset="-15"
          right
          no-caret
        >
          <template slot="button-content">
            <i aria-hidden="true" class="fas fa-ellipsis-v text-gray" />
          </template>
          <template v-if="canGeneratePassword(data.item)">
            <b-dropdown-item @click="generateNewPassword(data.item)">
              <span class="fa fa-key user-list__option-icon"></span>
              {{ $t('user-manager.query.list.options.generate-new-password') }}
            </b-dropdown-item>
            <b-dropdown-divider />
          </template>
          <b-dropdown-item
            :disabled="!$hasUserPermission('PER012')"
            @click="blockActivateUser(data.item)"
          >
            <span
              :class="
                `fa fa-${getBlockActivateIcon(
                  data.item,
                )} user-list__option-icon`
              "
            >
            </span>
            {{
              $t(
                'user-manager.query.list.options.' +
                  getBlockActivateTranslatePath(data.item),
              )
            }}
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </s-table>
    <grid-paginator
      :total-rows="totalItems"
      :per-page="perPage"
      v-model="currentPage"
      @input="paging()"
    />
    <action-to-require-password-modal
      title="user-manager.generate-new-password-modal.title"
      message="user-manager.generate-new-password-modal.message"
      @submit="submitResetPassword"
      @emit-modal-state="emitModalState"
      ref="generateNewPasswordModal"
    />
    <action-to-require-password-modal
      title="user-manager.delete-user-modal.title"
      message="user-manager.delete-user-modal.message"
      @submit="submitDeleteUser"
      @emit-modal-state="emitModalState"
      ref="deleteUserModal"
    />
    <action-to-require-password-modal
      :title="blockActivateUserModalTitle"
      :message="blockActivateUserModalMessage"
      @submit="submitBlockActivateUser"
      @emit-modal-state="emitModalState"
      ref="blockActivateUserModal"
    />
    <password-reset
      ref="passwordResetModal"
      @send-email="submitResetPasswordAndSendEmail()"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import GridPaginator from '@/components/lists/Paginator';
import ListAggregator from '@/components/listAggregator';
import { delayed } from '@/services/observables';
import Service from '@/domain/services/user';
import { status } from '@/domain/enums/user';
import ActionToRequirePasswordModal from '@/domain/components/generalPanel/adminPanel/user/modal/ActionToRequirePasswordModal';
import PasswordReset from '@/domain/components/generalPanel/adminPanel/user/editing/PasswordReset';

export default {
  props: {
    searchTerm: String,
  },
  components: {
    GridPaginator,
    ActionToRequirePasswordModal,
    PasswordReset,
    ListAggregator,
  },
  data: vm => ({
    loading: true,
    doDelayedRequest: delayed((...args) => vm.doRequest(...args)),
    currentPage: 1,
    totalItems: null,
    perPage: 10,
    items: [],
    sortBy: {
      field: 'name',
      desc: false,
    },
    fields: [
      {
        key: 'name',
        sortable: true,
        labelPath: 'name',
        class: 'user-list__cell-name',
      },
      {
        key: 'profiles',
        sortable: false,
        labelPath: 'access-profiles',
        class: 'truncate user-list__cell-profiles',
      },
      {
        key: 'username',
        sortable: true,
        labelPath: 'username',
        class: 'user-list__cell-username',
      },
      {
        key: 'status',
        sortable: true,
        labelPath: 'status',
        class: 'user-list__cell-status',
      },
      {
        key: 'level',
        sortable: true,
        labelPath: 'level',
        class: 'user-list__cell-level',
      },
      {
        key: 'actions',
        sortable: false,
        label: '',
        class: 'user-list__cell-actions',
      },
    ],
    profilesSelected: [],
    name: null,
    blockActivateUserModalTitle: '',
    blockActivateUserModalMessage: '',
    authenticatedUser: null,
    selectedUser: null,
  }),
  computed: {
    ...mapGetters({
      loggedUser: 'user',
    }),
    currentUser: {
      get() {
        return this.authenticatedUser || this.loggedUser;
      },
      set(value) {
        this.authenticatedUser = value;
      },
    },
    effectiveSearchTerm() {
      return this.searchTerm && this.searchTerm.length >= 3
        ? this.searchTerm
        : null;
    },
  },
  created() {
    this.refreshItems();
  },
  methods: {
    sort(evt) {
      this.sortBy.field = evt.sortBy;
      this.sortBy.desc = evt.sortDesc;
      this.currentPage = 1;
      this.refreshItems();
      return false;
    },
    paging() {
      this.refreshItems();
    },
    changingSearch() {
      this.currentPage = 1;
      this.refreshItems();
    },
    getBlockActivateTranslatePath(user) {
      switch (user.status) {
        case status.BLOCKED:
          return 'activate';
        case status.ACTIVE:
          return 'block';
        default:
          throw new Error(`Unknown status: "${user.status}".`);
      }
    },
    getStatusMod(user) {
      switch (user.status) {
        case status.BLOCKED:
          return 'blocked';
        case status.ACTIVE:
          return 'active';
        default:
          throw new Error(`Unknown status: "${user.status}".`);
      }
    },
    getStatusClass(user) {
      switch (user.status) {
        case status.BLOCKED:
          return 'text-red';
        case status.ACTIVE:
          return 'text-green';
        default:
          throw new Error(`Unknown status: "${user.status}".`);
      }
    },
    editUser(user) {
      this.$router.push({
        name: 'user.edit',
        params: {
          userId: user.id,
        },
      });
    },
    userIsLoggedUser(user) {
      return user.id === this.loggedUser.id;
    },
    deleteUser(user) {
      this.$refs.deleteUserModal.show(user);
    },
    submitDeleteUser(user) {
      Service.deleteUser({
        ...user,
        authenticatedUserPassword: user.password,
      })
        .then(() => this.$pushAlert({ code: 'MS007' }))
        .then(() => this.refreshItems());
    },
    canGeneratePassword(user) {
      return user.status === status.ACTIVE;
    },
    getBlockActivateIcon(user) {
      return user.status === status.ACTIVE ? 'user-lock' : 'user-check';
    },
    blockActivateUser(user) {
      if (user.status === status.ACTIVE) {
        this.blockActivateUserModalTitle = 'user-manager.block-activate-user-modal.title';
        this.blockActivateUserModalMessage = 'user-manager.block-activate-user-modal.message';
      } else {
        this.blockActivateUserModalTitle = 'user-manager.unblock-activate-user-modal.title';
        this.blockActivateUserModalMessage = 'user-manager.unblock-activate-user-modal.message';
      }

      this.$refs.blockActivateUserModal.show(user);
    },
    submitBlockActivateUser(user) {
      if (user.status === status.ACTIVE) {
        Service.blockUser(user.id, user.password)
          .then(() => this.$pushAlert({ code: 'MS005' }))
          .then(() => this.refreshItems());
      } else {
        Service.unblockUser(user.id, user.password)
          .then(() => this.$pushAlert({ code: 'MS004' }))
          .then(() => this.refreshItems());
      }
    },
    async refreshItems() {
      this.items = [];
      this.loading = true;
      try {
        return await this.doDelayedRequest(this.getRequestFilter());
      } finally {
        this.loading = false;
      }
    },
    doRequest(requestFilter) {
      return Service.getUsers(requestFilter).then(
        this.getProjectRequestResult(),
      );
    },
    getRequestFilter() {
      const sort = {};
      sort[this.sortBy.field] = this.sortBy.desc ? 'desc' : 'asc';
      return {
        filter: {
          name: this.effectiveSearchTerm,
        },
        page: this.currentPage,
        pageSize: this.perPage,
        sort,
      };
    },
    getProjectRequestResult() {
      return (paged) => {
        this.totalItems = paged.total;
        this.items = paged.data;
      };
    },
    showNewPassword(newPassword, eMail) {
      this.$refs.passwordResetModal.show(newPassword, eMail);
      this.$pushAlert({ code: 'MS002' });
    },
    showEmailSent(email) {
      this.$refs.passwordResetModal.showEmailSent(email);
      this.$pushAlert({ code: 'MS003' });
    },
    generateNewPassword(user) {
      this.selectedUser = user;
      this.$refs.generateNewPasswordModal.show(user);
    },
    submitResetPassword(user) {
      this.currentUser = user;
      Service.resetPassword(user.id, user.password)
        .then(result => this.showNewPassword(result.newPassword, result.targetEmail));
    },
    submitResetPasswordAndSendEmail() {
      const sendEmail = true;
      Service.resetPassword(this.selectedUser.id, this.currentUser.password, sendEmail)
        .then(result => this.showEmailSent(result.targetEmail));
    },
    emitModalState(state) {
      this.$emit('modals-open', state);
    },
  },
  watch: {
    effectiveSearchTerm() {
      this.changingSearch();
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"

.user-list
  &__captalized
    text-transform: capitalize
  &__options-btn
    margin: -10px 0
  &__option-icon
    margin-right: 5px
  ::v-deep &__cell
    &-name
      min-width: 100px
      width: 20%
      max-width: 350px
      overflow-wrap: break-word
    &-profiles
      width: 15%
    &-username
      width: 10%
    &-status
      width: 7%
    &-level
      width: 13%
    &-actions
      min-width: 15em
      width: 20%
  ::v-deep
    th.user-list__cell-profiles,
    th.user-list__cell-actions
      pointer-events: none

  +media-min-sm()
  +media-min-md()
  +media-min-lg()
</style>
